import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/cn";
import type { Period } from "@/api/types";

const OPTIONS: { value: Period; label: string }[] = [
  { value: "week", label: "Semana" },
  { value: "month_current", label: "Mes actual" },
  { value: "month_previous", label: "Mes pasado" },
  { value: "quarter", label: "Trimestre" },
  { value: "all", label: "Todo" },
];

export interface PeriodRadioProps {
  value: Period;
  onChange: (v: Period) => void;
}

/** Pill-style horizontal radio. A single shared-layout span slides
 * between segments so the active gradient "follows" the selection
 * instead of blinking in place. */
export function PeriodRadio({ value, onChange }: PeriodRadioProps) {
  const reduced = useReducedMotion();
  return (
    <div
      role="radiogroup"
      aria-label="Periodo"
      className="inline-flex gap-1 rounded-full border border-border-1 bg-white/[0.03] p-1"
    >
      {OPTIONS.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              "relative rounded-full px-4 py-1.5 text-sm font-semibold transition-colors",
              active ? "text-white" : "text-text-3 hover:text-text-1",
            )}
          >
            {active && (
              <motion.span
                layoutId="active-period"
                aria-hidden
                className="absolute inset-0 -z-0 rounded-full bg-gradient-to-br from-brand-1 to-brand-3 shadow-[0_8px_24px_-8px_rgba(236,72,153,0.5)]"
                transition={
                  reduced
                    ? { duration: 0 }
                    : { type: "spring", stiffness: 420, damping: 32 }
                }
              />
            )}
            <span className="relative z-10">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}
